import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DawaAddressInfo, DawaFormattedAddress, DawaSuggestionAddressData } from '../utils/map-dawa-suggestion';
import { formatDawaAddress } from '../utils/map-dawa-suggestion';

const DEFAULT_DEBOUNCE_MS = 200;
const DEFAULT_MIN_LENGTH = 3;
const DEFAULT_BASE_URL = 'https://dawa.aws.dk/autocomplete';

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export type UseDawaAutocompleteOptions = {
    debounceMs?: number;
    minLength?: number;
    adgangsAdresserOnly?: boolean;
    fuzzy?: boolean;
    stormodtagerpostnumre?: boolean;
    fetchDetails?: boolean;
    onError?: (error: unknown) => void;
    baseUrl?: string;
    fetchImpl?: FetchLike;
};

export type DawaAutocompleteResponse = DawaAutocompleteResponseItem[];

export type DawaAutocompleteResponseItem = {
    type: string;
    tekst: string;
    forslagstekst?: string;
    caretpos?: number;
    stormodtagerpostnr?: boolean;
    data: DawaSuggestionAddressData;
};

export type DawaEnrichment = {
    kvhx?: string;
    [key: string]: unknown;
};

export type DawaSuggestion = {
    id: string;
    type: string;
    tekst: string;
    forslagstekst?: string;
    value: string;
    label: string;
    href: string;
    address: DawaFormattedAddress['address'];
    addressInfo: DawaAddressInfo;
    data: DawaSuggestionAddressData;
    raw: DawaAutocompleteResponseItem;
};

export type UseDawaAutocompleteResult = {
    query: string;
    setQuery: (nextQuery: string) => void;
    suggestions: DawaSuggestion[];
    isLoading: boolean;
    error: unknown;
    activeSuggestion?: DawaSuggestion;
    selectSuggestion: (suggestion: DawaSuggestion) => Promise<{ suggestion: DawaSuggestion; enrichment?: DawaEnrichment }>;
    enrichSelection: (href: string) => Promise<DawaEnrichment | undefined>;
    reset: () => void;
    abort: () => void;
    refetch: () => void;
};

const ensureHttps = (href?: string): string => {
    if (!href) return '';
    return href.replace(/^http:/i, 'https:');
};

const buildQueryString = ({
    adgangsAdresserOnly,
    fuzzy,
    stormodtagerpostnumre,
    query,
}: {
    adgangsAdresserOnly: boolean;
    fuzzy: boolean;
    stormodtagerpostnumre: boolean;
    query: string;
}) => {
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('type', adgangsAdresserOnly ? 'adgangsadresse' : 'adresse');
    params.set('fuzzy', fuzzy ? 'true' : 'false');
    params.set('stormodtagerpostnumre', stormodtagerpostnumre ? 'true' : 'false');
    return params.toString();
};

const mapResponseItemToSuggestion = (item: DawaAutocompleteResponseItem): DawaSuggestion => {
    const normalizedHref = ensureHttps(item.data?.href);
    const formatted = formatDawaAddress(item.data ?? {});

    const id = item.data?.id ?? normalizedHref ?? `${item.type}-${item.tekst}`;
    const label = item.forslagstekst || item.tekst || formatted.address;
    const normalizedLabel = label ? label.replace(/\s+/g, ' ').trim() : '';
    const displayLabel = normalizedLabel || formatted.address;
    const value = displayLabel;

    return {
        id,
        type: item.type,
        tekst: item.tekst,
        forslagstekst: item.forslagstekst,
        value,
        label: displayLabel,
        href: normalizedHref,
        address: formatted.address,
        addressInfo: formatted.addressInfo,
        data: {
            ...(item.data ?? {}),
            href: normalizedHref,
        },
        raw: item,
    };
};

export const useDawaAutocomplete = (options: UseDawaAutocompleteOptions = {}): UseDawaAutocompleteResult => {
    const {
        debounceMs = DEFAULT_DEBOUNCE_MS,
        minLength = DEFAULT_MIN_LENGTH,
        adgangsAdresserOnly = false,
        fuzzy = true,
        stormodtagerpostnumre = false,
        fetchDetails = true,
        onError,
        baseUrl = DEFAULT_BASE_URL,
        fetchImpl,
    } = options;

    const fetcherRef = useRef<FetchLike | undefined>(fetchImpl);
    fetcherRef.current = fetchImpl;

    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<DawaSuggestion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<unknown>(undefined);
    const [activeSuggestion, setActiveSuggestion] = useState<DawaSuggestion | undefined>(undefined);

    const abortControllerRef = useRef<AbortController | undefined>(undefined);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const isMountedRef = useRef(true);

    const abort = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = undefined;
    }, []);

    const reset = useCallback(() => {
        abort();
        setQuery('');
        setSuggestions([]);
        setIsLoading(false);
        setError(undefined);
        setActiveSuggestion(undefined);
    }, [abort]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            abort();
            if (debounceTimerRef.current !== undefined) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [abort]);

    const runFetch = useCallback(
        async (currentQuery: string) => {
            abort();

            if (currentQuery.length < minLength) {
                if (!isMountedRef.current) return;
                setSuggestions([]);
                setIsLoading(false);
                return;
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                setIsLoading(true);
                setError(undefined);
                const url = `${baseUrl}?${buildQueryString({
                    adgangsAdresserOnly,
                    fuzzy,
                    stormodtagerpostnumre,
                    query: currentQuery,
                })}`;

                const fetchLike = fetcherRef.current ?? fetch;
                const response = await fetchLike(url, {
                    signal: controller.signal,
                    headers: { Accept: 'application/json' },
                });

                if (!response.ok) {
                    throw new Error(`DAWA request failed with status ${response.status}`);
                }

                const payload = (await response.json()) as DawaAutocompleteResponse;
                if (!Array.isArray(payload)) {
                    throw new Error('Unexpected DAWA response shape');
                }

                if (!isMountedRef.current) return;

                setSuggestions(payload.map(mapResponseItemToSuggestion));
            } catch (err) {
                if ((err as Error)?.name === 'AbortError') {
                    return;
                }
                if (!isMountedRef.current) return;
                setSuggestions([]);
                setError(err);
                onError?.(err);
            } finally {
                if (!isMountedRef.current) return;
                setIsLoading(false);
            }
        },
        [abort, adgangsAdresserOnly, baseUrl, fuzzy, minLength, onError, stormodtagerpostnumre]
    );

    const refetch = useCallback(() => {
        runFetch(query);
    }, [query, runFetch]);

    useEffect(() => {
        if (debounceTimerRef.current !== undefined) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = undefined;
        }

        if (!query) {
            setSuggestions([]);
            setIsLoading(false);
            return;
        }

        const timer = setTimeout(() => {
            runFetch(query);
        }, debounceMs);

        debounceTimerRef.current = timer;

        return () => {
            if (debounceTimerRef.current !== undefined) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = undefined;
            }
        };
    }, [debounceMs, query, runFetch]);

    const enrichSelection = useCallback(
        async (href: string): Promise<DawaEnrichment | undefined> => {
            const normalizedHref = ensureHttps(href);
            if (!normalizedHref) return undefined;

            const fetchLike = fetcherRef.current ?? fetch;
            try {
                const response = await fetchLike(normalizedHref, { headers: { Accept: 'application/json' } });
                if (!response.ok) {
                    throw new Error(`DAWA enrichment failed with status ${response.status}`);
                }
                const data = (await response.json()) as DawaEnrichment;
                return data;
            } catch (err) {
                onError?.(err);
                return undefined;
            }
        },
        [onError]
    );

    const selectSuggestion = useCallback(
        async (suggestion: DawaSuggestion) => {
            setActiveSuggestion(suggestion);
            if (!fetchDetails) {
                return { suggestion };
            }

            const enrichment = await enrichSelection(suggestion.href);
            return { suggestion, enrichment };
        },
        [enrichSelection, fetchDetails]
    );

    return useMemo(
        () => ({
            query,
            setQuery,
            suggestions,
            isLoading,
            error,
            activeSuggestion,
            selectSuggestion,
            enrichSelection,
            reset,
            abort,
            refetch,
        }),
        [abort, activeSuggestion, enrichSelection, error, isLoading, query, refetch, reset, selectSuggestion, suggestions]
    );
};

export type { DawaAddressInfo };
